
import { supabase } from "@/integrations/supabase/client";
import { StoreEmployee } from "@/models/storeModel";
import { v4 as uuidv4 } from "uuid";
import { toast } from "@/hooks/use-toast";
import {
  logEmployeeUpdated,
  logEmployeeDeleted,
} from "./employeeLoggingService";
import invitationService from "./invitationService";

// Add employee function - updated to use the separated approach
export const addEmployee = async (
  employeeData: Omit<StoreEmployee, "id" | "createdAt" | "updatedAt" | "userId">
): Promise<StoreEmployee> => {
  try {
    console.log("Creating employee invitation with data:", employeeData);

    // Create the invitation first
    const invitation = await invitationService.createInvitation({
      firstName: employeeData.firstName,
      lastName: employeeData.lastName,
      email: employeeData.email,
      role: employeeData.role,
      storeId: employeeData.storeId,
      address: employeeData.address,
      street: employeeData.street,
      streetNumber: employeeData.streetNumber,
      city: employeeData.city,
      postcode: employeeData.postcode,
    });

    console.log("New employee invitation created:", invitation);

    // Then attempt to send the email
    if (invitation && invitation.id && invitation.token) {
      try {
        const emailResult = await invitationService.sendInvitationEmail({
          invitationId: invitation.id,
          token: invitation.token,
          email: employeeData.email,
          firstName: employeeData.firstName,
          lastName: employeeData.lastName,
          role: employeeData.role,
          storeId: employeeData.storeId,
        });

        console.log("Email sending result:", emailResult);
      } catch (emailError) {
        // We don't throw here to avoid failing the whole operation
        // The invitation was created successfully, even if the email failed
        console.error("Error sending invitation email:", emailError);
      }
    }

    // For now, return a partial object that conforms to StoreEmployee
    return {
      userId: invitation.id, // Fixed: Add userId property
      id: invitation.id,
      storeId: employeeData.storeId,
      firstName: employeeData.firstName,
      lastName: employeeData.lastName,
      email: employeeData.email,
      role: employeeData.role as StoreEmployee["role"],
      address: employeeData.address,
      street: employeeData.street,
      streetNumber: employeeData.streetNumber,
      city: employeeData.city,
      postcode: employeeData.postcode,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error("Error in addEmployee:", error);
    throw error;
  }
};

// Update an employee's role in Supabase
export const updateEmployeeRole = async (
  id: string,
  role: "admin" | "manager" | "staff" | "super_admin"
): Promise<StoreEmployee> => {
  // Get original data for comparison
  const { data: originalEmployee, error: fetchError } = await supabase
    .from("store_employees")
    .select("*")
    .eq("id", id)
    .single();

  if (fetchError) {
    console.error("Error fetching original employee data:", fetchError);
    throw fetchError;
  }

  const { data, error } = await supabase
    .from("store_employees")
    .update({ role })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating employee role:", error);
    throw error;
  }

  // Log the role change
  if (originalEmployee.role !== role) {
    await logEmployeeUpdated(id, data.store_id, {
      role: [originalEmployee.role, role],
    });
  }

  return {
    id: data.id,
    storeId: data.store_id,
    userId: data.user_id,
    firstName: data.first_name,
    lastName: data.last_name,
    email: data.email,
    role: data.role,
    address: data.address,
    street: data.street,
    streetNumber: data.street_number,
    city: data.city,
    postcode: data.postcode,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
};

// Remove an employee from Supabase
export const removeEmployee = async (id: string): Promise<boolean> => {
  // First, get the employee record to find the user_id
  const { data: employee, error: fetchError } = await supabase
    .from("store_employees")
    .select("*")
    .eq("id", id)
    .single();

  if (fetchError) {
    console.error("Error fetching employee:", fetchError);
    throw fetchError;
  }

  // Log employee removal before actual deletion
  await logEmployeeDeleted(id, employee.store_id, {
    firstName: employee.first_name,
    lastName: employee.last_name,
    email: employee.email,
    role: employee.role,
  });

  // Delete the employee record
  const { error: deleteError } = await supabase
    .from("store_employees")
    .delete()
    .eq("id", id);

  if (deleteError) {
    console.error("Error removing employee:", deleteError);
    throw deleteError;
  }

  // Also delete the corresponding user record
  if (employee && employee.user_id) {
    const { error: userDeleteError } = await supabase
      .from("users")
      .delete()
      .eq("id", employee.user_id);

    if (userDeleteError) {
      console.error("Error removing user:", userDeleteError);
      // We still return true because the employee was successfully removed
    }
  }

  return true;
};
